import type { FormInstance } from 'antd'
import { LatLng } from 'leaflet'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { SwtchAddress } from '../services/data-provider/geocoding'

const MANUAL_TYPING_DEBOUNCE_TIMEOUT = 1500

export interface ParsedAddressFromPlace {
  address?: string
  city?: string
  postalCode?: string
  province?: string
  country?: string
  latitude?: number
  longitude?: number
}

export interface UseAddressAutocompleteOptions {
  form: FormInstance
  setAddressForGeocoding: (address: SwtchAddress) => void
  setGeocodedLocation: (location: LatLng | undefined) => void
  setPinnedAddress: (address: string) => void
  initializeTimeZone: (lat: number, lng: number) => void | Promise<void>
  /** Initial country for autocomplete restriction (e.g. 'CA'). Default 'CA'. */
  initialCountry?: string
  /** When true, form has latitude/longitude fields to update on place select (tenant location tab). Default false (tenant create). */
  hasLatLongFormFields?: boolean
}

export interface AddressAutocompleteProps {
  country: string | undefined
  onPlaceSelected: (parsedAddress: ParsedAddressFromPlace) => void
  onChange: () => void
}

/**
 * Reusable hook for address input with Google Places autocomplete.
 * Used by tenant location tab and tenant create page.
 */
export function useAddressAutocomplete(options: UseAddressAutocompleteOptions) {
  const {
    form,
    setAddressForGeocoding,
    setGeocodedLocation,
    setPinnedAddress,
    initializeTimeZone,
    initialCountry = 'CA',
    hasLatLongFormFields = false,
  } = options

  const [currentCountry, setCurrentCountry] = useState<string | undefined>(initialCountry)
  const manualTypingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isFromAutocompleteRef = useRef<boolean>(false)

  useEffect(() => {
    return () => {
      if (manualTypingTimeoutRef.current) {
        clearTimeout(manualTypingTimeoutRef.current)
      }
    }
  }, [])

  const triggerDebouncedGeocoding = useCallback(() => {
    if (manualTypingTimeoutRef.current) clearTimeout(manualTypingTimeoutRef.current)
    manualTypingTimeoutRef.current = setTimeout(() => {
      const formValues = form.getFieldsValue()
      const address = formValues.address
      const city = formValues.city
      const postalCode = formValues.postalCode
      if (address && city && postalCode) {
        const currentLat = formValues.latitude
        const currentLng = formValues.longitude
        if (!currentLat || !currentLng) {
          isFromAutocompleteRef.current = false
          setAddressForGeocoding({
            numberAndStreet: address,
            city,
            postalCode,
            province: formValues.province?.[1] || '',
            country: formValues.province?.[0] || '',
          })
        }
      }
    }, MANUAL_TYPING_DEBOUNCE_TIMEOUT)
  }, [form, setAddressForGeocoding])

  const onPlaceSelected = useCallback(
    (parsedAddress: ParsedAddressFromPlace) => {
      const updates: Record<string, unknown> = {}
      if (parsedAddress.address) updates.address = parsedAddress.address
      if (parsedAddress.city) updates.city = parsedAddress.city
      if (parsedAddress.postalCode) updates.postalCode = parsedAddress.postalCode

      const currentProvince = form.getFieldValue('province')
      const currentCountryVal = currentProvince?.[0]
      const currentProvinceCode = currentProvince?.[1]
      if (parsedAddress.country && parsedAddress.province) {
        if (
          parsedAddress.country !== currentCountryVal ||
          parsedAddress.province !== currentProvinceCode
        ) {
          updates.province = [parsedAddress.country, parsedAddress.province]
        }
      } else if (parsedAddress.country && parsedAddress.country !== currentCountryVal) {
        updates.province = [parsedAddress.country, currentProvinceCode || '']
      }
      if (Object.keys(updates).length > 0) form.setFieldsValue(updates)

      if (manualTypingTimeoutRef.current) {
        clearTimeout(manualTypingTimeoutRef.current)
        manualTypingTimeoutRef.current = null
      }

      if (
        parsedAddress.latitude !== undefined &&
        parsedAddress.longitude !== undefined
      ) {
        if (hasLatLongFormFields) {
          form.setFieldsValue({
            latitude: parsedAddress.latitude,
            longitude: parsedAddress.longitude,
          })
        }
        setGeocodedLocation(new LatLng(parsedAddress.latitude, parsedAddress.longitude))
        setPinnedAddress(parsedAddress.address || '')
        initializeTimeZone(parsedAddress.latitude, parsedAddress.longitude)
      } else {
        isFromAutocompleteRef.current = true
        setAddressForGeocoding({
          numberAndStreet: parsedAddress.address || '',
          city: parsedAddress.city || '',
          postalCode: parsedAddress.postalCode || '',
          province: form.getFieldValue('province')?.[1] || '',
          country: form.getFieldValue('province')?.[0] || '',
        })
      }
    },
    [
      form,
      hasLatLongFormFields,
      setAddressForGeocoding,
      setGeocodedLocation,
      setPinnedAddress,
      initializeTimeZone,
    ],
  )

  const addressAutocompleteProps: AddressAutocompleteProps = {
    country: currentCountry,
    onPlaceSelected,
    onChange: triggerDebouncedGeocoding,
  }

  return {
    currentCountry,
    setCurrentCountry,
    addressAutocompleteProps,
    triggerDebouncedGeocoding,
    isFromAutocompleteRef,
  }
}
